import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const faqs = [
  {
    q: "How do I get started with RideX City?",
    a: "Simply download the RideX City app, create your profile, locate an available electric scooter nearby, and unlock it instantly with a QR scan.",
  },
  {
    q: "What documents do I need to sign up?",
    a: "You just need a standard government photo ID for a quick one-time digital verification inside the app. No physical paperwork or office visit required.",
  },
  {
    q: "Is insurance included with my rental?",
    a: "Yes. Every ride and every subscription plan comes with free insurance cover, so you're protected in case of an accident or vehicle damage during a valid trip.",
  },
  {
    q: "What happens if my vehicle breaks down mid-ride?",
    a: "Our end-to-end breakdown support team is a tap away in the app. We'll arrange a swap or on-spot fix, and the time lost to a breakdown is never billed to you.",
  },
  {
    q: "Where can I charge the vehicle?",
    a: "RideX City vehicles use independent, swappable battery packs, so you're not tied to a single charging point. Nearby swap and charging stations are shown live in the app.",
  },
  {
    q: "How do I start an EV leasing business or invest?",
    a: "You can submit your requirements in our EV Leasing form above or invest capital that earns returns across a fixed 36-month payout schedule as our fleet operates.",
  },
  {
    q: "Which areas in Bangalore do you operate in?",
    a: "We currently serve Whitefield, Electronic City, and the Outer Ring Road (ORR) corridor, with new zones added as fleet availability grows.",
  },
  {
    q: "How do I reach RideX City support?",
    a: "You can call or WhatsApp us directly at +91 81231 98675, or reach out through the in-app help centre for ride, billing, or leasing queries.",
  },
];

export default function FAQSection() {
  const [expandedId, setExpandedId] = useState(0);

  const handleChange = (id) => (event, isExpanded) => {
    setExpandedId(isExpanded ? id : null);
  };

  return (
    <Box id="faq" component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: "background.paper" }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: "inline-block",
                px: 2,
                py: 0.6,
                borderRadius: "20px",
                bgcolor: "rgba(27, 78, 216, 0.08)",
                color: "primary.main",
                fontSize: 12.5,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              FREQUENTLY ASKED QUESTIONS
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: { xs: 28, md: 36 },
                fontWeight: 700,
                color: "text.primary",
                mb: 2,
                letterSpacing: "-0.02em",
              }}
            >
              Got Questions?{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                We Have Answers.
              </Box>
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 15.5, lineHeight: 1.7 }}>
              Still unsure about something? Call or WhatsApp us directly at{" "}
              <Box
                component="a"
                href="tel:+918123198675"
                sx={{ color: "primary.main", fontWeight: 700, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                +91 81231 98675
              </Box>{" "}
              and our support team will guide you.
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            {faqs.map((faq, i) => {
              const isSelected = expandedId === i;
              return (
                <Accordion
                  key={faq.q}
                  expanded={isSelected}
                  onChange={handleChange(i)}
                  disableGutters
                  elevation={0}
                  square
                  sx={{
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    borderLeft: isSelected ? "4px solid #1B4ED8" : "1px solid #E2E8F0",
                    borderRadius: "14px !important",
                    overflow: "hidden",
                    mb: 1.75,
                    transition: "all 0.2s ease",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{ color: isSelected ? "secondary.dark" : "text.secondary" }}
                      />
                    }
                    sx={{ px: 3, py: 0.75, "& .MuiAccordionSummary-content": { my: 1.5 } }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 700,
                        fontSize: 16,
                        color: isSelected ? "primary.main" : "text.primary",
                      }}
                    >
                      {faq.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}>
                    <Typography sx={{ fontSize: 14.5, color: "text.secondary", lineHeight: 1.7 }}>
                      {faq.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
